<template>
  <SplitterGroup
    :auto-save-id="LocalStorageKey.DRAFT_EDITOR_PANELS"
    :direction="isPlayground ? 'vertical' : 'horizontal'"
  >
    <SplitterPanel
      v-show="showLeftPane"
      id="left"
      :min-size="25"
      class="flex flex-col"
    >
      <slot name="panel-1"></slot>
    </SplitterPanel>

    <SplitterResizeHandle
      v-if="showLeftPane && props.showRight"
      class="group"
      :class="
        isPlayground
          ? 'border-b border-b-gray-300'
          : 'border-l border-l-gray-300'
      "
    >
      <div
        v-if="!isPlayground"
        class="group-focus:outline absolute bg-neutral-200 -ml-2.5 z-20 rounded top-[calc(50%-1rem)] p-0.5 hover:bg-neutral-300 transition-colors cursor-grab"
      >
        <Bars3Icon class="size-4 rotate-90 text-neutral-400" />
      </div>
    </SplitterResizeHandle>

    <SplitterPanel
      v-if="props.showRight"
      id="right"
      :min-size="25"
      class="flex flex-col"
    >
      <slot name="panel-2"></slot>
    </SplitterPanel>
  </SplitterGroup>
</template>

<script lang="ts" setup>
import { SplitterGroup, SplitterPanel, SplitterResizeHandle } from "radix-vue";
import { computed, ref, watch } from "vue";

import Bars3Icon from "@/icons/heroicons/bars-3-outline.svg";
import { useMediaQuery } from "@/lib/media_query";
import {
  LocalStorageKey,
  safeLocalStorageGetItem,
} from "@/store/local_storage";

export interface Props {
  showRight?: boolean;
}

const props = defineProps<Props>();

const isMobile = useMediaQuery("(width < 768px)");
const isPlayground = computed(() => {
  return (
    new URLSearchParams(window.location.search).get("show_preview") === "true"
  );
});
const showLeftPane = computed(
  () => !props.showRight || !isMobile.value || isPlayground.value
);
</script>

<script lang="ts">
const BREAKPOINT = 768;
const QUERY_PARAMETER = "show_preview";

export const useShowingPreview = () => {
  const isOnMobile = window.innerWidth < BREAKPOINT;
  const shouldShowPreviewOverride = new URLSearchParams(
    window.location.search
  ).get(QUERY_PARAMETER);

  const initialValue =
    shouldShowPreviewOverride === "true"
      ? true
      : shouldShowPreviewOverride === "false"
      ? false
      : !isOnMobile &&
        safeLocalStorageGetItem(LocalStorageKey.SHOWING_PREVIEW) === "true";

  // Only consider the persisted value if we're not on mobile, that way, mobile
  // will never have the preview pane shown on mount
  const showingPreview = ref(initialValue);

  watch(
    () => showingPreview.value,
    (value) => {
      localStorage.setItem(
        LocalStorageKey.SHOWING_PREVIEW,
        value ? "true" : "false"
      );
    }
  );

  return showingPreview;
};
</script>
